import { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Stack, Tooltip } from '@mui/material';
import GridViewIcon from '@mui/icons-material/GridView';
import ScatterPlotIcon from '@mui/icons-material/ScatterPlot';
import { useTranslation } from 'react-i18next';
import { PmViewToggle } from '../../../components/pm';
import { clampLevel, jitterOffset, levelToPixel, pixelToLevel } from './riskScatterMath';

export interface MatrixRisiko {
  id: string;
  beschreibung: string;
  weight: number; // Schwere / Auswirkung (1-5)
  assessment: number; // Eintrittswahrscheinlichkeit (1-5)
}

const LEVELS = [1, 2, 3, 4, 5];

/** Ampel-Stufe einer Zelle nach Score (= Schwere × Eintritt). Identisch zur Tabellen-Logik. */
function cellTone(score: number): 'success' | 'warning' | 'error' {
  if (score <= 6) return 'success';
  if (score <= 14) return 'warning';
  return 'error';
}

const TONE_VAR: Record<'success' | 'warning' | 'error', string> = {
  success: 'var(--pm-success)',
  warning: 'var(--pm-warning)',
  error: 'var(--pm-error)',
};

// Scatter-Ansicht: Rastermasse (groesser als die klassischen 52x44-Zellen).
const CELL_W = 70;
const CELL_H = 60;
const GRID_W = CELL_W * 5;
const GRID_H = CELL_H * 5;
const DOT_SIZE = 20;
const JITTER_SPREAD = 12;

/** X-Pixel (0..GRID_W) fuer eine Eintrittswahrscheinlichkeit-Stufe 1-5. */
function xForAssessment(a: number): number {
  return levelToPixel(a, GRID_W);
}

/** Y-Pixel (0..GRID_H) fuer eine Schwere-Stufe 1-5 — 5 oben, 1 unten (wie das Zellraster). */
function yForWeight(w: number): number {
  return GRID_H - levelToPixel(w, GRID_H);
}

/** Kehrt xForAssessment/yForWeight um, inkl. Rundung/Klemmen auf 1-5. */
function assessmentForX(x: number): number {
  return pixelToLevel(clampPx(x, GRID_W), GRID_W);
}
function weightForY(y: number): number {
  return pixelToLevel(GRID_H - clampPx(y, GRID_H), GRID_H);
}
function clampPx(px: number, max: number): number {
  return Math.min(max, Math.max(0, px));
}

interface DragState {
  id: string;
  startClientX: number;
  startClientY: number;
  origX: number;
  origY: number;
}

export type RisikoMoveHandler = (id: string, weight: number, assessment: number) => void;

/**
 * Risikomatrix (P-08): 5×5-Zellraster (Anzahl je Zelle) plus umschaltbare
 * Scatter-Ansicht mit einzelnen, per Drag verschiebbaren Risiko-Punkten.
 * Ziehen eines Punkts aktualisiert Eintrittswahrscheinlichkeit (X) und
 * Schwere (Y) — gesnappt auf ganze Stufen 1-5, gemeldet ueber `onMove`.
 *
 * KRITISCH: `score` ist im Backend eine GENERATED COLUMN (weight × assessment)
 * und wird hier NIE gelesen oder mitgesendet — die Ampel-Farbe wird lokal aus
 * weight × assessment berechnet, `onMove` liefert ausschliesslich {weight, assessment}.
 */
export function RisikoMatrix({ items, onMove }: { items: MatrixRisiko[]; onMove?: RisikoMoveHandler }) {
  const { t } = useTranslation('projects');
  const [mode, setMode] = useState<'cells' | 'scatter'>('cells');

  // Zellen-Index: "weight×assessment" -> Risiken in dieser Zelle
  const byCell = useMemo(() => {
    const map = new Map<string, MatrixRisiko[]>();
    for (const r of items) {
      const w = clampLevel(r.weight);
      const a = clampLevel(r.assessment);
      const key = `${w}x${a}`;
      const list = map.get(key) ?? [];
      list.push(r);
      map.set(key, list);
    }
    return map;
  }, [items]);

  const axisLabel = { fontSize: 11, color: 'var(--pm-fg-subtle)', fontWeight: 600 };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 0.5 }}>
        <Box>
          <Typography variant="subtitle2" sx={{ color: 'var(--pm-fg-base)' }}>
            {t('risiken.matrix.title', 'Risikomatrix')}
          </Typography>
          <Typography variant="caption" sx={{ color: 'var(--pm-fg-muted)', display: 'block', mb: 1.5 }}>
            {mode === 'cells'
              ? t('risiken.matrix.hint', 'Verteilung aller Risiken nach Schwere (Y) und Eintrittswahrscheinlichkeit (X). Zahl = Anzahl Risiken in dieser Zelle.')
              : t('risiken.matrix.scatterHint', 'Jeder Punkt ist ein Risiko. Ziehen ändert Schwere (Y) und Eintrittswahrscheinlichkeit (X) — beim Loslassen wird auf ganze Stufen gerundet und gespeichert.')}
          </Typography>
        </Box>
        <PmViewToggle<'cells' | 'scatter'>
          value={mode}
          onChange={setMode}
          options={[
            { value: 'cells', label: t('risiken.matrix.viewCells', 'Zellen'), icon: <GridViewIcon fontSize="small" /> },
            { value: 'scatter', label: t('risiken.matrix.viewScatter', 'Punkte'), icon: <ScatterPlotIcon fontSize="small" /> },
          ]}
        />
      </Stack>

      <Stack direction="row" spacing={1}>
        {/* Y-Achsentitel (vertikal) */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography sx={{ ...axisLabel, writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
            {t('risiken.weight', 'Schwere')} →
          </Typography>
        </Box>

        <Box>
          <Box sx={{ position: 'relative' }}>
            {/* Grid: Y oben=5 absteigend, X links=1 aufsteigend */}
            {[...LEVELS].reverse().map((w) => (
              <Stack direction="row" key={w} alignItems="stretch">
                <Box sx={{ width: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', ...axisLabel }}>
                  {w}
                </Box>
                {LEVELS.map((a) => {
                  const score = w * a;
                  const tone = cellTone(score);
                  const toneVar = TONE_VAR[tone];
                  const cell = byCell.get(`${w}x${a}`) ?? [];
                  const count = cell.length;
                  const showCount = mode === 'cells' && count > 0;
                  return (
                    <Tooltip
                      key={a}
                      arrow
                      disableHoverListener={mode !== 'cells' || count === 0}
                      title={
                        count === 0 ? '' : (
                          <Box>
                            {cell.map((r) => (
                              <Typography key={r.id} variant="caption" sx={{ display: 'block' }}>
                                • {r.beschreibung || '—'}
                              </Typography>
                            ))}
                          </Box>
                        )
                      }
                    >
                      <Box
                        sx={{
                          width: CELL_W - 4,
                          height: CELL_H - 4,
                          m: '2px',
                          borderRadius: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: `color-mix(in srgb, ${toneVar} ${count > 0 ? 32 : 16}%, transparent)`,
                          border: `1px solid color-mix(in srgb, ${toneVar} ${count > 0 ? 70 : 35}%, transparent)`,
                          cursor: showCount ? 'default' : 'inherit',
                        }}
                      >
                        {showCount ? (
                          <Typography sx={{ fontWeight: 700, fontSize: 16, color: toneVar, lineHeight: 1 }}>
                            {count}
                          </Typography>
                        ) : (
                          <Typography sx={{ fontSize: 10, color: 'var(--pm-fg-subtle)', opacity: 0.5, lineHeight: 1 }}>
                            {score}
                          </Typography>
                        )}
                      </Box>
                    </Tooltip>
                  );
                })}
              </Stack>
            ))}

            {mode === 'scatter' && (
              <RisikoScatterOverlay items={items} onMove={onMove} />
            )}
          </Box>

          {/* X-Achse: Werte + Titel */}
          <Stack direction="row">
            <Box sx={{ width: 18 }} />
            {LEVELS.map((a) => (
              <Box key={a} sx={{ width: CELL_W, textAlign: 'center', ...axisLabel }}>{a}</Box>
            ))}
          </Stack>
          <Box sx={{ pl: '18px' }}>
            <Typography sx={{ ...axisLabel, textAlign: 'center' }}>
              {t('risiken.assessment', 'Eintrittswahrscheinlichkeit')} →
            </Typography>
          </Box>
        </Box>
      </Stack>

      {/* Legende */}
      <Stack direction="row" spacing={2} sx={{ mt: 1.5, flexWrap: 'wrap' }}>
        {([
          ['success', t('risiken.matrix.low', 'Gering (≤ 6)')],
          ['warning', t('risiken.matrix.medium', 'Mittel (7–14)')],
          ['error', t('risiken.matrix.high', 'Hoch (> 14)')],
        ] as const).map(([tone, label]) => (
          <Stack key={tone} direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: `color-mix(in srgb, ${TONE_VAR[tone]} 60%, transparent)`, border: `1px solid ${TONE_VAR[tone]}` }} />
            <Typography variant="caption" sx={{ color: 'var(--pm-fg-muted)' }}>{label}</Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

/**
 * Absolut positionierte Punktwolke ueber dem 5×5-Zellraster. Rein pixelbasiert
 * (keine DOM-Messung noetig) — Positionen ergeben sich direkt aus weight/assessment
 * via riskScatterMath. Ueberlappende Punkte erhalten einen deterministischen Jitter.
 */
function RisikoScatterOverlay({ items, onMove }: { items: MatrixRisiko[]; onMove?: RisikoMoveHandler }) {
  const { t } = useTranslation('projects');
  // Lokale Overrides fuer soeben gedroppte Punkte, bis der Server-Refetch die Props einholt.
  const [overrides, setOverrides] = useState<Record<string, { weight: number; assessment: number }>>({});
  const [drag, setDrag] = useState<DragState | null>(null);
  const [dragPx, setDragPx] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    setOverrides((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const item of items) {
        const ov = next[item.id];
        if (ov && ov.weight === item.weight && ov.assessment === item.assessment) {
          delete next[item.id];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [items]);

  const posFor = (item: MatrixRisiko): { weight: number; assessment: number } =>
    overrides[item.id] ?? { weight: clampLevel(item.weight), assessment: clampLevel(item.assessment) };

  // Jitter-Gruppen nach aktueller (nicht-gedragter) Position berechnen.
  const grouped = useMemo(() => {
    const map = new Map<string, MatrixRisiko[]>();
    for (const item of items) {
      if (drag && drag.id === item.id) continue; // waehrend Drag folgt der Punkt dem Cursor, kein Jitter
      const { weight, assessment } = posFor(item);
      const key = `${weight}x${assessment}`;
      const list = map.get(key) ?? [];
      list.push(item);
      map.set(key, list);
    }
    return map;
  }, [items, overrides, drag]);

  useEffect(() => {
    if (!drag) return;
    const onMove_ = (e: MouseEvent) => {
      const x = clampPx(drag.origX + (e.clientX - drag.startClientX), GRID_W);
      const y = clampPx(drag.origY + (e.clientY - drag.startClientY), GRID_H);
      setDragPx({ x, y });
    };
    const onUp = (e: MouseEvent) => {
      const x = clampPx(drag.origX + (e.clientX - drag.startClientX), GRID_W);
      const y = clampPx(drag.origY + (e.clientY - drag.startClientY), GRID_H);
      const weight = weightForY(y);
      const assessment = assessmentForX(x);
      const d = drag;
      setDrag(null);
      setDragPx(null);
      const orig = items.find((i) => i.id === d.id);
      if (orig && (clampLevel(orig.weight) !== weight || clampLevel(orig.assessment) !== assessment)) {
        setOverrides((prev) => ({ ...prev, [d.id]: { weight, assessment } }));
        onMove?.(d.id, weight, assessment);
      }
    };
    window.addEventListener('mousemove', onMove_);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove_);
      window.removeEventListener('mouseup', onUp);
    };
  }, [drag, items, onMove]);

  const startDrag = (e: React.MouseEvent, item: MatrixRisiko) => {
    e.preventDefault();
    e.stopPropagation();
    const { weight, assessment } = posFor(item);
    const origX = xForAssessment(assessment);
    const origY = yForWeight(weight);
    setDrag({ id: item.id, startClientX: e.clientX, startClientY: e.clientY, origX, origY });
    setDragPx({ x: origX, y: origY });
  };

  const moveByKeyboard = (item: MatrixRisiko, dWeight: number, dAssessment: number) => {
    const { weight, assessment } = posFor(item);
    const nextWeight = clampLevel(weight + dWeight);
    const nextAssessment = clampLevel(assessment + dAssessment);
    if (nextWeight === weight && nextAssessment === assessment) return;
    setOverrides((prev) => ({ ...prev, [item.id]: { weight: nextWeight, assessment: nextAssessment } }));
    onMove?.(item.id, nextWeight, nextAssessment);
  };

  return (
    <Box
      data-testid="risiko-scatter-track"
      sx={{
        position: 'absolute',
        left: 18, // Y-Achsenbeschriftung links ausgleichen (siehe Grid oben)
        top: 0,
        width: GRID_W,
        height: GRID_H,
        pointerEvents: 'none',
      }}
    >
      {items.map((item) => {
        const isDragging = drag?.id === item.id;
        const { weight, assessment } = posFor(item);
        const score = clampLevel(weight) * clampLevel(assessment);
        const tone = cellTone(score);
        const toneVar = TONE_VAR[tone];

        let cx: number;
        let cy: number;
        if (isDragging && dragPx) {
          cx = dragPx.x;
          cy = dragPx.y;
        } else {
          const cellKey = `${weight}x${assessment}`;
          const cellItems = grouped.get(cellKey) ?? [item];
          const idxInCell = cellItems.findIndex((r) => r.id === item.id);
          const { dx, dy } = jitterOffset({ index: Math.max(0, idxInCell), total: cellItems.length }, JITTER_SPREAD);
          cx = xForAssessment(assessment) + dx;
          cy = yForWeight(weight) + dy;
        }

        const label = `${item.beschreibung || t('risiken.beschreibung', 'Beschreibung')} — ${t('risiken.weight', 'Schwere')} ${weight}, ${t('risiken.assessment', 'Eintrittswahrscheinlichkeit')} ${assessment}`;

        return (
          <Tooltip key={item.id} title={item.beschreibung || ''} arrow>
            <Box
              role="button"
              tabIndex={0}
              aria-label={label}
              data-testid={`risiko-point-${item.id}`}
              data-cx={cx}
              data-cy={cy}
              onMouseDown={(e) => startDrag(e, item)}
              onKeyDown={(e) => {
                if (e.key === 'ArrowUp') { e.preventDefault(); moveByKeyboard(item, 1, 0); }
                else if (e.key === 'ArrowDown') { e.preventDefault(); moveByKeyboard(item, -1, 0); }
                else if (e.key === 'ArrowRight') { e.preventDefault(); moveByKeyboard(item, 0, 1); }
                else if (e.key === 'ArrowLeft') { e.preventDefault(); moveByKeyboard(item, 0, -1); }
              }}
              sx={{
                position: 'absolute',
                left: cx - DOT_SIZE / 2,
                top: cy - DOT_SIZE / 2,
                width: DOT_SIZE,
                height: DOT_SIZE,
                borderRadius: '50%',
                bgcolor: toneVar,
                border: '2px solid var(--pm-bg-base)',
                boxShadow: isDragging ? '0 2px 8px rgba(0,0,0,0.35)' : '0 1px 3px rgba(0,0,0,0.25)',
                cursor: isDragging ? 'grabbing' : 'grab',
                pointerEvents: 'auto',
                zIndex: isDragging ? 2 : 1,
                transition: isDragging ? 'none' : 'left 0.15s ease, top 0.15s ease',
                '&:focus-visible': { outline: '2px solid var(--pm-primary-hover)', outlineOffset: 1 },
              }}
            />
          </Tooltip>
        );
      })}
    </Box>
  );
}
